import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { PatientAccessRequestInbox } from '../../components/PatientAccessRequestInbox';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '../../components/Toaster';
import {
  ArrowLeft, Upload, Trash2, Shield, FileImage, FileText,
  Eye, X, Loader2, ShieldCheck, Clock, Building2, AlertCircle, Share2, UserRound
} from 'lucide-react';
import api from '../../utils/api';
import { useContent } from '../../hooks/useContent';
import SecureShareDialog from '../../components/SecureShareDialog';
import RevokeConfirmDialog from '../../components/RevokeConfirmDialog';

// API base for absolute thumbnail URLs (backend returns paths like /api/attachments/{id}?variant=thumbnail)
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// MEDIA-001-BIS (2026-02-28): native-fetch helper for protected document
// previews. Replaces `api.get(..., {responseType:'blob'})` so CRA's
// react-error-overlay never accesses `xhr.responseText` on a non-2xx blob
// response (the cross-origin DOMException that surfaces as "Script error.").
// Token is the canonical patient/unified token used everywhere else.
const _readPatientToken = () =>
  (typeof window !== 'undefined' &&
    (window.localStorage?.getItem('treatx_patient_token') ||
      window.localStorage?.getItem('treatx_unified_token'))) ||
  '';

// SEC-MEDICAL-FILE-INTERNAL-AUTHZ-01 / UX-ROLE-LABEL-SALES-REP-01 —
// patient-readable recipient role labels. DISPLAY ONLY: canonical role ids
// are never replaced. Copy lives in the TreatX i18n bundles under
// `patient.medicalFiles.recipientRoles.*` (EN/TR parity).
const RECIPIENT_ROLE_LABEL_KEY = (role) =>
  `patient.medicalFiles.recipientRoles.${role}`;

const FileIcon = ({ type }) => {
  if (['jpg', 'jpeg', 'png'].includes(type)) return <FileImage className="h-5 w-5 text-blue-500" />;
  if (type === 'pdf') return <FileText className="h-5 w-5 text-red-500" />;
  return <FileText className="h-5 w-5 text-slate-400" />;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Extract a human-readable error message from an axios error response
const extractErrorMessage = (err, fallback = 'An unexpected error occurred') => {
  const data = err?.response?.data;
  if (data?.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  if (data?.error) return data.error;
  if (err?.message) return err.message;
  return fallback;
};

const MedicalFilesPage = () => {
  const { t } = useTranslation();
  // i18next returns the key itself when a bundle entry is missing, so fall
  // back to the canonical role id rather than leaking a translation key.
  const recipientRoleLabel = (role) => {
    const key = RECIPIENT_ROLE_LABEL_KEY(role);
    const label = t(key);
    return label && label !== key ? label : role;
  };
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // MEDIA-001-BIS: AbortController for the in-flight preview fetch, and a
  // ref-tracked active blob URL so we revoke the previous preview even when
  // the user opens a new one without first closing the modal (no leak).
  const previewAbortRef = useRef(null);
  const previewUrlRef = useRef(null);

  // CMS content
  const { content: securityBadgeContent } = useContent('medical_files_security_badge');
  const { content: gdprNoticeContent } = useContent('medical_files_gdpr_notice');

  // TREATX-SECURE-MEDICAL-FILE-SHARING-10B: deliberate share state.
  // Sharing from the vault is a *distinct* action — never triggered by
  // upload. The dialog requires the patient to pick a recipient (one of
  // their existing clinic conversations), pick a purpose, and tick the
  // consent checkbox before any `attachment_access` grant is created.
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareFiles, setShareFiles] = useState([]);
  const [recipientOptions, setRecipientOptions] = useState([]);

  // TREATX-10B-UX-ACCEPTANCE-FIX: styled revoke confirmation (replaces
  // window.confirm). Keeps "stop sharing this file" + reassurance copy
  // + clinic name visible to the patient before they confirm.
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokePayload, setRevokePayload] = useState(null);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);

  const fetchFiles = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await api.get('/patient/medical-files');
      // axios: success → res.status 2xx; data on res.data
      if (res.data?.success) {
        setFiles(res.data.files || []);
      } else {
        setLoadError('Could not load your files');
        setFiles([]);
      }
    } catch (err) {
      console.error('Fetch files error:', err);
      setLoadError(extractErrorMessage(err, 'Could not load your files'));
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    if (selected.length > 5) {
      toast.error('Too many files', { description: 'Max 5 files per upload' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      selected.forEach(f => formData.append('files', f));

      // axios.post(url, body, config) — pass FormData as body; axios sets multipart boundary automatically
      const res = await api.post('/patient/medical-files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data || {};

      if (!data.success) {
        toast.error('Upload failed', {
          description: data.detail || data.error || 'Server rejected the upload',
        });
        return;
      }

      const ok = (data.files || []).filter(f => !f.error);
      const bad = (data.files || []).filter(f => f.error);

      if (ok.length) {
        toast.success('Upload complete', {
          description: `${ok.length} file(s) uploaded securely`,
        });
      }
      if (bad.length) {
        toast.error('Some files failed', {
          description: bad.map(f => `${f.file_name}: ${f.error}`).join('; '),
        });
      }
      if (!ok.length && !bad.length) {
        // Defensive: should never happen given backend contract, but surface honestly
        toast.error('No files were saved', {
          description: 'The server did not persist any files. Please try again.',
        });
      }

      // Re-fetch authoritative state so the UI reflects what's actually in DB
      await fetchFiles();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed', {
        description: extractErrorMessage(err, 'Network or server error during upload'),
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Delete this file? This will also revoke all clinic access.')) return;
    try {
      const res = await api.delete(`/patient/medical-files/${attachmentId}`);
      if (res.data?.success) {
        toast.success('Deleted', { description: 'File removed and all access revoked' });
        await fetchFiles();
      } else {
        toast.error('Delete failed', {
          description: res.data?.detail || 'Server did not confirm deletion',
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Delete failed', {
        description: extractErrorMessage(err, 'Failed to delete file'),
      });
    }
  };

  // TREATX-SECURE-MEDICAL-FILE-SHARING-10B: open the Secure Share dialog
  // for a single file. Calls the dedicated /patient/share-recipients
  // endpoint so the picker only ever shows real clinic names resolved
  // from clinic_profiles — never raw provider IDs or staff emails.
  // UAT-MEDICAL-JOURNEY-SCOPED-AUTHZ-01: the vault selects a JOURNEY, not
  // merely a Provider. `/patient/share-recipients` returns one option per
  // patient-owned conversation group, so two journeys at the same Provider
  // (e.g. Hair Transplant and Dental) are two distinguishable choices,
  // each carrying its own exact source thread and its own recipients.
  const handleOpenShare = async (file) => {
    try {
      const resp = await api.get('/patient/share-recipients');
      const list = (resp?.data?.recipients) || [];
      setRecipientOptions(list.map(r => ({
        clinic_id: r.clinic_id,
        clinic_name: r.clinic_name || 'Clinic',
        conversation_group_id: r.conversation_group_id,
        thread_id: r.thread_id || null,
        treatment: r.treatment || '',
        status: r.status || '',
        journey_label: r.journey_label || r.clinic_name || 'Clinic',
        name_resolved: r.name_resolved !== false,
        // Eligible (user_id, role) relationships of THIS journey, derived
        // server-side from the patient's persisted lineage only.
        people: r.people || [],
      })));
    } catch (err) {
      console.error('Failed to load share recipients', err);
      setRecipientOptions([]);
    }
    setShareFiles([file]);
    setShareDialogOpen(true);
  };

  const handleRevoke = (attachmentId, clinicId, clinicName, fileName, recipient) => {
    // TREATX-10B-UX-ACCEPTANCE-FIX: open the styled confirmation dialog
    // instead of the native window.confirm. The real revoke API call
    // happens in confirmRevoke() once the patient clicks "Stop sharing"
    // inside RevokeConfirmDialog.
    //
    // SEC-MEDICAL-FILE-INTERNAL-AUTHZ-01 — revocation is recipient-level.
    // `recipient` omitted = "stop sharing with this provider" bulk action
    // (revokes every active recipient grant for that provider).
    setRevokePayload({
      attachmentId,
      clinicId,
      clinicName,
      fileName,
      recipientUserId: recipient?.recipient_user_id || null,
      recipientName: recipient?.recipient_name || null,
    });
    setRevokeOpen(true);
  };

  const confirmRevoke = async () => {
    if (!revokePayload) return;
    const { attachmentId, clinicId, clinicName, recipientUserId, recipientName } = revokePayload;
    const label = recipientName || clinicName || 'this clinic';
    setRevokeSubmitting(true);
    try {
      const res = await api.post('/patient/attachments/revoke', {
        attachment_id: attachmentId,
        clinic_id: clinicId,
        recipient_user_id: recipientUserId || undefined,
      });
      if (res.data?.success) {
        toast.success('Access stopped', {
          description: `${label} can no longer view this file.`,
        });
        setRevokeOpen(false);
        setRevokePayload(null);
        await fetchFiles();
      } else {
        toast.error('Could not stop sharing', {
          description: res.data?.detail || 'Please try again.',
        });
      }
    } catch (err) {
      console.error('Revoke error:', err);
      toast.error('Could not stop sharing', {
        description: extractErrorMessage(err, 'Failed to stop sharing'),
      });
    } finally {
      setRevokeSubmitting(false);
    }
  };

  // MEDIA-001-BIS (2026-02-28): hardened protected-preview fetch.
  //   * native fetch() — CRA overlay never sniffs xhr.responseText.
  //   * AbortController — closing the modal mid-fetch aborts cleanly.
  //   * deferred revoke of previous object URL (50 ms) — React 18 strict
  //     mode + back-to-back previews can't drop a still-rendering <img>.
  //   * explicit failure state — toast + visible "Failed to load preview".
  // Same /api/attachments/{id} endpoint, same auth header, same RBAC
  // (backend enforces patient owner + grant rules).
  const handlePreview = async (file) => {
    // Abort any in-flight preview from a previous click.
    if (previewAbortRef.current) {
      try { previewAbortRef.current.abort(); } catch { /* noop */ }
    }
    // Defer-revoke the previous blob URL so any still-rendering <img> can
    // finish using it before it becomes invalid.
    const previousUrl = previewUrlRef.current;
    if (previousUrl) {
      setTimeout(() => {
        try { URL.revokeObjectURL(previousUrl); } catch { /* noop */ }
      }, 50);
    }
    previewUrlRef.current = null;

    setPreviewFile(file);
    setPreviewLoading(true);
    setPreviewUrl(null);

    const controller = new AbortController();
    previewAbortRef.current = controller;

    const token = _readPatientToken();
    const url = `${API_BASE}/api/attachments/${encodeURIComponent(file.attachment_id)}`;
    try {
      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
        headers: token ? {} : undefined,
        signal: controller.signal,
      });
      if (!resp.ok) {
        const message = resp.status === 401 || resp.status === 403
          ? 'Not authorised to preview this file'
          : resp.status === 404
            ? 'File not found on the server'
            : `Could not load preview (HTTP ${resp.status})`;
        toast.error('Preview failed', { description: message });
        return;
      }
      const blob = await resp.blob();
      // Bail if the user closed the modal / clicked another file mid-fetch.
      if (controller.signal.aborted) return;
      const objectUrl = URL.createObjectURL(blob);
      previewUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);
    } catch (err) {
      if (err && err.name === 'AbortError') return; // closed mid-fetch, ignore
      // Local failure — never let this bubble to window.onerror.
      // eslint-disable-next-line no-console
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[MedicalFilesPage.handlePreview] suppressed fetch error', err);
      }
      toast.error('Preview failed', {
        description: (err && err.message) || 'Could not load preview',
      });
    } finally {
      if (previewAbortRef.current === controller) previewAbortRef.current = null;
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    // Abort any in-flight preview fetch.
    if (previewAbortRef.current) {
      try { previewAbortRef.current.abort(); } catch { /* noop */ }
      previewAbortRef.current = null;
    }
    // Defer-revoke so the active <img> can finish painting in strict mode.
    const url = previewUrlRef.current;
    previewUrlRef.current = null;
    if (url) {
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      }, 50);
    }
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  // MEDIA-001-BIS: unmount cleanup — abort + revoke if the user navigates
  // away while a preview is still loading or open.
  useEffect(() => {
    return () => {
      if (previewAbortRef.current) {
        try { previewAbortRef.current.abort(); } catch { /* noop */ }
        previewAbortRef.current = null;
      }
      const url = previewUrlRef.current;
      previewUrlRef.current = null;
      if (url) {
        setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
        }, 0);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="medical-files-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 pb-12" data-testid="medical-files-page">
      <Toaster />

      {/* Header */}
      <header className="bg-white border-b mt-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="icon" onClick={() => navigate('/patient/dashboard')} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-testid="medical-files-title">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  {t('patient.medicalFiles.pageHeader')}
                </h1>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{securityBadgeContent?.body || t('patient.common.loading')}</p>
              </div>
            </div>
            <div className="shrink-0">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleUpload}
                className="hidden"
                data-testid="file-upload-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="upload-files-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {uploading ? t('patient.documents.uploading') : t('patient.medicalFiles.uploadCta')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* GDPR Notice */}
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3.5 mb-3" data-testid="gdpr-notice">
          <Shield className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-800">
            <p className="font-medium mb-0.5">{t('patient.medicalFiles.protectionCardTitle')}</p>
            <p className="text-emerald-600">{gdprNoticeContent?.body || t('patient.medicalFiles.protectionCardBody')}</p>
          </div>
        </div>

        {/* TREATX-10B-UX-ACCEPTANCE-FIX: explicit access-vs-retention
            explainer so the patient doesn't conflate the 90-day vault
            retention with the active sharing grant. */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6"
          data-testid="access-duration-explainer"
        >
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
              {t('patient.medicalFiles.retentionCardTitle')}
            </p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              {t('patient.medicalFiles.retentionCardBody')}
            </p>
          </div>
          <div className="bg-white border border-blue-200 rounded-lg p-3">
            <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide">
              {t('patient.medicalFiles.sharingCardTitle')}
            </p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              {t('patient.medicalFiles.sharingCardBody')}
            </p>
          </div>
        </div>

        {/* Load error banner — truthful failure state */}
        <PatientAccessRequestInbox onDecided={fetchFiles} />
        {loadError && (
          <div
            className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3.5 mb-6"
            data-testid="files-load-error"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="text-xs text-red-800 flex-1">
              <p className="font-medium mb-0.5">{t('patient.common.error')}</p>
              <p className="text-red-600">{loadError}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchFiles}
              className="border-red-300 text-red-700 hover:bg-red-100"
              data-testid="retry-load-files-btn"
            >
              {t('patient.common.retry')}
            </Button>
          </div>
        )}

        {/* Files List */}
        {!loadError && files.length === 0 ? (
          <div className="text-center py-16" data-testid="no-files-state">
            <FileImage className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-slate-700 mb-1">{t('patient.medicalFiles.empty')}</h3>
            <p className="text-xs text-slate-500 mb-4">{t('patient.medicalFiles.subtitle')}</p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              data-testid="upload-first-file-btn"
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-1.5" />
              {t('patient.medicalFiles.uploadCta')}
            </Button>
          </div>
        ) : files.length > 0 ? (
          <div className="space-y-3" data-testid="files-list">
            {files.map(file => (
              <Card key={file.attachment_id} className="border border-slate-200 shadow-sm" data-testid={`file-card-${file.attachment_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Thumbnail or Icon */}
                    {file.thumbnail_url ? (
                      <div
                        className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 shrink-0 cursor-pointer bg-slate-50"
                        onClick={() => handlePreview(file)}
                        data-testid={`thumbnail-${file.attachment_id}`}
                      >
                        <img
                          src={`${API_BASE}${file.thumbnail_url}`}
                          alt={file.file_name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={e => { e.target.style.display = 'none'; e.target.parentNode.classList.add('flex', 'items-center', 'justify-center'); }}
                        />
                      </div>
                    ) : (
                      <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                        <FileIcon type={file.file_type} />
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800 truncate" data-testid={`file-name-${file.attachment_id}`}>{file.file_name}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{file.file_type?.toUpperCase()}</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500 flex-wrap">
                        <span>{formatSize(file.file_size)}</span>
                        {/* TREATX-10B-UX-ACCEPTANCE-FIX: clarify the
                            difference between vault retention (90 days)
                            and active sharing (until revoked). */}
                        <span
                          className="flex items-center gap-1"
                          title={t('patient.medicalFiles.retentionTooltip')}
                          data-testid={`file-retention-${file.attachment_id}`}
                        >
                          <Clock className="h-3 w-3" />
                          Kept in vault until {formatDateLocale(file.expires_at)}
                        </span>
                      </div>

                      {/* Active recipient grants — SEC-MEDICAL-FILE-INTERNAL-AUTHZ-01.
                          Access is person-level: each badge is one named
                          person, with their own revoke control. */}
                      {file.access_grants?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide mb-1">
                            Who can open this file — access stays until you stop it
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {file.access_grants.map((grant, i) => (
                              <Badge
                                key={grant.recipient_user_id || i}
                                className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] pr-1 flex items-center gap-1"
                                data-testid={`access-grant-${grant.recipient_user_id || grant.clinic_id}`}
                              >
                                <UserRound className="h-2.5 w-2.5" />
                                <span title={`Shared since ${grant.granted_at ? formatDateLocale(grant.granted_at) : ''}`}>
                                  {grant.recipient_name || 'Recipient'}
                                  {grant.recipient_role ? ` · ${recipientRoleLabel(grant.recipient_role)}` : ''}
                                  {grant.clinic_name ? ` · ${grant.clinic_name}` : ''}
                                </span>
                                <button
                                  onClick={() => handleRevoke(file.attachment_id, grant.clinic_id, grant.clinic_name, file.file_name, grant)}
                                  className="ml-0.5 hover:bg-blue-100 rounded-full p-0.5"
                                  title={`Stop sharing this file with ${grant.recipient_name || 'this person'}`}
                                  data-testid={`revoke-btn-${grant.recipient_user_id || grant.clinic_id}`}
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                          {/* Bulk action — revokes EVERY active recipient
                              grant for that provider. It never restores
                              provider-wide authority. */}
                          {[...new Set(file.access_grants.map(g => g.clinic_id))].map(cid => {
                            const cname = file.access_grants.find(g => g.clinic_id === cid)?.clinic_name;
                            return (
                              <button
                                key={cid}
                                onClick={() => handleRevoke(file.attachment_id, cid, cname, file.file_name, null)}
                                className="mt-1.5 text-[10px] text-red-600 hover:text-red-700 underline block"
                                data-testid={`revoke-all-btn-${cid}`}
                              >
                                Stop sharing with everyone at {cname || 'this clinic'}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {(!file.access_grants || file.access_grants.length === 0) && (
                        <p
                          className="mt-2 text-[10px] text-slate-400 italic"
                          data-testid={`no-active-sharing-${file.attachment_id}`}
                        >
                          Not shared with anyone yet.
                        </p>
                      )}

                      {/* Sharing history — every grant ever issued for this
                          file, with recipient, role, date and status. */}
                      {file.share_history?.length > 0 && (
                        <details className="mt-2" data-testid={`share-history-${file.attachment_id}`}>
                          <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700">
                            Sharing history ({file.share_history.length})
                          </summary>
                          <ul className="mt-1 space-y-1">
                            {file.share_history.map((g, i) => (
                              <li
                                key={g.recipient_user_id ? `${g.recipient_user_id}-${g.granted_at}` : i}
                                className="text-[10px] text-slate-600 flex items-center gap-1.5 flex-wrap"
                                data-testid={`share-history-row-${g.recipient_user_id || 'legacy'}-${i}`}
                              >
                                <span className="font-medium text-slate-800">
                                  {g.recipient_name || 'Provider-wide (inactive)'}
                                </span>
                                {g.recipient_role && (
                                  <span className="text-slate-500">
                                    {recipientRoleLabel(g.recipient_role)}
                                  </span>
                                )}
                                <span className="text-slate-400">·</span>
                                <span>{g.clinic_name || 'Clinic'}</span>
                                <span className="text-slate-400">·</span>
                                <span>{g.granted_at ? formatDateLocale(g.granted_at) : ''}</span>
                                <Badge
                                  variant="outline"
                                  className={`text-[9px] ${
                                    g.status === 'active'
                                      ? 'text-emerald-700 border-emerald-200 bg-emerald-50'
                                      : 'text-slate-500 border-slate-200 bg-slate-50'
                                  }`}
                                >
                                  {g.status === 'active' ? 'Active'
                                    : g.status === 'revoked' ? 'Stopped'
                                      : g.status === 'expired' ? 'Expired' : 'Inactive'}
                                </Badge>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>

                    {/* Actions — TREATX-10B-UX-ACCEPTANCE-FIX:
                        labelled buttons so ordinary patients can read
                        the affordance without guessing at icons. */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-slate-600"
                        onClick={() => handlePreview(file)}
                        data-testid={`preview-btn-${file.attachment_id}`}
                        title={t('patient.medicalFiles.viewTitle')}
                      >
                        <Eye className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline text-xs">{t('patient.medicalFiles.actionView')}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                        onClick={() => handleOpenShare(file)}
                        data-testid={`share-btn-${file.attachment_id}`}
                        title={t('patient.medicalFiles.shareTitle')}
                      >
                        <Share2 className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline text-xs">{t('patient.medicalFiles.actionShare')}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(file.attachment_id)}
                        data-testid={`delete-btn-${file.attachment_id}`}
                        title={t('patient.documents.delete')}
                      >
                        <Trash2 className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline text-xs">{t('patient.documents.delete')}</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closePreview} data-testid="preview-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b">
              <span className="text-sm font-medium text-slate-700 truncate">{previewFile.file_name}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={closePreview} data-testid="close-preview-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 flex items-center justify-center min-h-[300px]">
              {previewLoading ? (
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              ) : previewUrl ? (
                ['jpg', 'jpeg', 'png'].includes(previewFile.file_type) ? (
                  <img src={previewUrl} alt={previewFile.file_name} className="max-w-full max-h-[70vh] object-contain rounded" data-testid="preview-image" />
                ) : (
                  <div className="text-center">
                    <FileText className="h-12 w-12 text-red-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 mb-2">{t('patient.medicalFiles.actionView')}</p>
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm underline" data-testid="preview-open-pdf">
                      Open PDF
                    </a>
                  </div>
                )
              ) : (
                <p className="text-sm text-slate-500">{t('patient.common.error')}</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* TREATX-SECURE-MEDICAL-FILE-SHARING-10B: Secure Share dialog
          (Vault flow). Sharing from the vault REQUIRES the patient to
          pick a recipient from their existing clinic conversations. We
          show clinic names only — never clinic_id / thread_id.
          Conditionally mounted so every open starts with clean state. */}
      {shareDialogOpen && (
        <SecureShareDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setShareFiles([]);
          }}
          onShared={async () => {
            await fetchFiles();
          }}
          files={shareFiles}
          surface="medical_vault"
          recipientOptions={recipientOptions}
        />
      )}
      {/* TREATX-10B-UX-ACCEPTANCE-FIX: styled revoke confirmation. */}
      {revokeOpen && (
        <RevokeConfirmDialog
          open={revokeOpen}
          clinicName={revokePayload?.recipientName || revokePayload?.clinicName}
          fileName={revokePayload?.fileName}
          submitting={revokeSubmitting}
          onConfirm={confirmRevoke}
          onClose={() => {
            if (!revokeSubmitting) {
              setRevokeOpen(false);
              setRevokePayload(null);
            }
          }}
        />
      )}
    </div>
  );
};

export default MedicalFilesPage;